// src/pages/modals/ApplicationFullViewModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import rawAxios from "axios";
import axios from "../../api/axios";
import "./ApplicationFullViewModal.css";

const API_URL = process.env.REACT_APP_API_URL || "https://expertise.gov.kz";

/* ===== helpers ===== */
const openExternal = (url) => {
  if (!url) return;
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (!opened) {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
};

const isPresignedMinioUrl = (url) => {
  if (!url) return false;
  const lower = String(url).toLowerCase();
  return (
    lower.includes("x-amz-signature=") ||
    lower.includes("x-amz-algorithm=") ||
    lower.includes("x-amz-credential=") ||
    lower.includes("minio.expertise.gov.kz")
  );
};

const joinUrl = (base, path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${String(base).replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`;
};

const getFilenameFromDisposition = (disposition) => {
  if (!disposition) return null;
  const utf = /filename\*=([^']*)''([^;]+)/i.exec(disposition);
  if (utf?.[2]) return decodeURIComponent(utf[2]);
  const ascii = /filename="?([^";]+)"?/i.exec(disposition);
  return ascii?.[1] || null;
};

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/* ===== i18n ===== */
const L = {
  kz: {
    title: "Өтініш туралы толық ақпарат",
    common: "Жалпы мәлімет",
    params: "Параметрлер",
    people: "Тағайындаулар",
    files: "Файлдар",
    publisher: "Баспа (шифр)",
    status: "Күйі",
    pageCount: "Бет саны",
    sheetCount: "Парақ саны",
    duration: "Ұзақтығы (мин)",
    category: "Санат",
    criteriaForm: "Түрі",
    head: "Басшы",
    internal: "Ішкі сарапшы",
    download: "Жүктеу",
    downloadArchive: "Архив жүктеу",
    link: "Сілтеме",
    noFiles: "Файлдар жоқ",
    name: "Аты-жөні",
    email: "Email",
    loading: "Жүктелуде...",
    error: "Қате орын алды",
  },
  ru: {
    title: "Полная информация по заявке",
    common: "Общие сведения",
    params: "Параметры",
    people: "Назначения",
    files: "Файлы",
    publisher: "Издательство (шифр)",
    status: "Статус",
    pageCount: "Кол-во страниц",
    sheetCount: "Кол-во листов",
    duration: "Длительность (мин)",
    category: "Категория",
    criteriaForm: "Форма",
    head: "Руководитель",
    internal: "Внутренний эксперт",
    download: "Скачать",
    downloadArchive: "Скачать архив",
    link: "Ссылка",
    noFiles: "Файлов нет",
    name: "Имя",
    email: "Email",
    loading: "Загрузка...",
    error: "Произошла ошибка",
  },
};

const pickName = (obj, locale) =>
  obj ? obj[`name_${locale}`] || obj.name || obj.title || obj.id || "—" : "—";

export default function ApplicationFullViewModal({ appId, onClose, locale = "kz" }) {
  const t = useMemo(() => L[locale] || L.kz, [locale]);

  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // { [key]: { pct: number|null } }
  const [busy, setBusy] = useState({});

  const token = localStorage.getItem("token");
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const setBusyState = (key, pct) =>
    setBusy((prev) => ({ ...prev, [key]: { pct } }));

  const clearBusyState = (key) =>
    setBusy((prev) => { const c = { ...prev }; delete c[key]; return c; });

  /* ===== data fetch ===== */
  useEffect(() => {
    if (!appId) return;
    let active = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const res = await axios.get(
          `/backoffice/applications/${appId}`,
          { headers: authHeaders }
        );
        const payload = res?.data ?? {};
        const base = payload.application ?? payload.data ?? payload;
        const merged = {
          ...base,
          assigned_head: payload.assigned_head ?? base.assigned_head,
          assigned_internal_expert:
            payload.assigned_internal_expert ?? base.assigned_internal_expert,
          files: payload.files ?? base.files ?? [],
          status: payload.status ?? base.status,
        };
        if (active) setApp(merged);
      } catch (e) {
     
        if (active) setErr(t.error);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, locale]);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  /* ===== download ===== */
  const downloadByUrl = async (url, suggestedName, key) => {
    const fullUrl = joinUrl(API_URL, url);
    const isMinio = isPresignedMinioUrl(fullUrl);

    try {
      setBusyState(key, null);

      const onDownloadProgress = (e) => {
        const total = e?.total || 0;
        const loaded = e?.loaded || 0;
        if (total > 0) {
          setBusyState(key, Math.max(0, Math.min(100, Math.round((loaded * 100) / total))));
        } else {
          setBusyState(key, null); // индетерминат
        }
      };

      let res;
      if (isMinio) {
        // MinIO presigned URL — без Authorization, иначе 403
        res = await rawAxios.get(fullUrl, { responseType: "blob", onDownloadProgress });
      } else {
        res = await axios.get(fullUrl, {
          responseType: "blob",
          headers: authHeaders,
          onDownloadProgress,
        });
      }

      const disposition =
        res.headers?.["content-disposition"] ||
        res.headers?.get?.("content-disposition");
      const filename = getFilenameFromDisposition(disposition) || suggestedName;
      saveBlob(res.data, filename);
    } catch (e) {
  
      if (isMinio) {
        // fallback — открыть напрямую
        openExternal(fullUrl);
      } else {
        alert(locale === "kz"
          ? "Файлды жүктеу мүмкін болмады"
          : "Не удалось скачать файл"
        );
      }
    } finally {
      clearBusyState(key);
    }
  };

  /* ===== render ===== */
  return (
    <div className="app-modal-overlay" onClick={onClose}>
      <div className="app-modal" onClick={(e) => e.stopPropagation()}>
        <button className="app-modal-close" onClick={onClose}>×</button>
        <h2>{t.title}</h2>

        {loading && <p>{t.loading}</p>}
        {err && <p className="error">{err}</p>}

        {app && (
          <div className="app-modal-body">

            {/* Общие сведения */}
            <section className="app-block">
              <h3>{t.common}</h3>
              <div className="app-grid">
                <div className="lbl">{t.status}</div>
                <div>{pickName(app.status, locale)}</div>
                <div className="lbl">{t.publisher}</div>
                <div>{app.publisher || "—"}</div>
              </div>
            </section>

            {/* Параметры */}
            <section className="app-block">
              <h3>{t.params}</h3>
              <div className="app-grid">
                <div className="lbl">{t.category}</div>
                <div>{pickName(app.category, locale)}</div>
                <div className="lbl">{t.criteriaForm}</div>
                <div>{pickName(app.criteria_form, locale)}</div>
                <div className="lbl">{t.pageCount}</div>
                <div>{app.page_count || "—"}</div>
                <div className="lbl">{t.sheetCount}</div>
                <div>{app.sheet_count || "—"}</div>
                <div className="lbl">{t.duration}</div>
                <div>{app.duration_minutes || "—"}</div>
              </div>
            </section>

            {/* Люди */}
            <section className="app-block">
              <h3>{t.people}</h3>
              <div className="app-people">
                <div className="card">
                  <div className="card-title">{t.head}</div>
                  <div><b>{t.name}:</b> {app.assigned_head?.name || "—"}</div>
                  <div><b>{t.email}:</b> {app.assigned_head?.email || "—"}</div>
                </div>
                <div className="card">
                  <div className="card-title">{t.internal}</div>
                  <div><b>{t.name}:</b> {app.assigned_internal_expert?.name || "—"}</div>
                  <div><b>{t.email}:</b> {app.assigned_internal_expert?.email || "—"}</div>
                </div>
              </div>
            </section>

            {app?.word && (
  <section className="app-block">
    <h3>{locale === "kz" ? "Доработка файлы" : "Файл доработки"}</h3>
    <ul className="app-file-list">
      <li className="file-row">
        <div className="file-meta">
          <span className="file-name">{app.word?.original_name || "document.docx"}</span>
        </div>
        <div className="file-actions">
          <button
            type="button"
            className="btn-download"
            onClick={() => downloadByUrl(
              `api/applications/${appId}/download-word`,
              app.word?.original_name || "document.docx",
              "word"
            )}
          >
            ⬇ {t.download}
          </button>
        </div>
      </li>
    </ul>
  </section>
)}

            {/* Файлы */}
            <section className="app-block">
              <h3>{t.files}</h3>

              {app.files?.length ? (
                <ul className="app-file-list">
                  {app.files.map((f, index) => {
                    // --- ключи прогресса ---
                    const fileKey = String(f.id ?? index);
                    const archiveKey = `archive_${fileKey}`;

                    const fileBusy    = busy[fileKey];
                    const archiveBusy = busy[archiveKey];

                    const isFileLoading    = !!fileBusy;
                    const isArchiveLoading = !!archiveBusy;

                    // --- флаги файла ---
                    const isLink = f.is_link === true;

                    // PDF / обычный файл
                    const downloadUrl = !isLink
                      ? (f.download_url || f.url || "")
                      : "";
                    const hasDownload = !!downloadUrl;

                    // внешняя ссылка
                    const linkUrl = f.link_url || f.external_url || "";
                    const hasLink = isLink && !!linkUrl;

                    // архив секции
                    const archiveUrl =
                      f.section_archive_download_url ||
                      f.section_archive?.download_url ||
                      "";
                    const archiveName =
                      f.section_archive_name ||
                      f.section_archive?.original_name ||
                      `section_${f.section ?? index + 1}.zip`;
                    const hasArchive = !!archiveUrl;

                    const sectionLabel = f.name || `Бөлім ${f.section ?? index + 1}`;

                    return (
                      <li key={f.id ?? index} className="file-row">
                        <div className="file-meta">
                          <span className="file-name">{sectionLabel}</span>
                          <span className="file-sub">
                            {[
                              hasDownload && "PDF",
                              hasLink && t.link,
                              hasArchive && (locale === "kz" ? "Архив бар" : "Есть архив"),
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </div>

                        <div className="file-actions">
                          {/* Внешняя ссылка */}
                          {hasLink && (
                            <button
                              type="button"
                              className="btn-link"
                              onClick={() => openExternal(linkUrl)}
                            >
                              🔗 {t.link}
                            </button>
                          )}

                          {/* Скачать PDF */}
                          {hasDownload && (
                            <button
                              type="button"
                              className="btn-download"
                              disabled={isFileLoading || isArchiveLoading}
                              onClick={() =>
                                downloadByUrl(
                                  downloadUrl,
                                  `file_${f.id ?? index}.pdf`,
                                  fileKey
                                )
                              }
                            >
                              {isFileLoading ? (
                                <>
                                  <span className="spinner" />
                                  {t.download}{" "}
                                  {fileBusy?.pct !== null && fileBusy?.pct !== undefined
                                    ? `${fileBusy.pct}%`
                                    : "..."}
                                </>
                              ) : (
                                <>⬇ {t.download}</>
                              )}
                            </button>
                          )}

                          {/* Скачать архив */}
                          {hasArchive && (
                            <button
                              type="button"
                              className="btn-download btn-download--archive"
                              disabled={isFileLoading || isArchiveLoading}
                              onClick={() =>
                                downloadByUrl(archiveUrl, archiveName, archiveKey)
                              }
                            >
                              {isArchiveLoading ? (
                                <>
                                  <span className="spinner" />
                                  {t.downloadArchive}{" "}
                                  {archiveBusy?.pct !== null && archiveBusy?.pct !== undefined
                                    ? `${archiveBusy.pct}%`
                                    : "..."}
                                </>
                              ) : (
                                <>📦 {t.downloadArchive}</>
                              )}
                            </button>
                          )}
                        </div>

                        {/* Прогресс-бар */}
                        {(isFileLoading || isArchiveLoading) && (() => {
                          const activeBusy = fileBusy || archiveBusy;
                          const pct = activeBusy?.pct;
                          return (
                            <div className={`dl-bar ${pct === null ? "indeterminate" : ""}`}>
                              <div
                                className="dl-bar__fill"
                                style={{ "--w": `${pct ?? 0}%` }}
                              />
                            </div>
                          );
                        })()}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="muted">{t.noFiles}</p>
              )}
            </section>

          </div>
        )}
      </div>
    </div>
  );
}